/**
 * Centre Health → Preschool Quality & Ops (Satya, 28 Oct 2026): the
 * Preschool Ops tiles and the Teacher Scorecard (was Centre Audit → Teacher
 * Scorecard) on one tab. One Centre and one Period picker drive both. The
 * two are tabs (Satya, 28 Oct pm: not cards you click into); both views stay
 * mounted (hidden when not open) so switching keeps each view's state.
 *
 * The scorecard tab shows only to roles holding centreAudit.teacherScorecard
 * — the same tick as before the move; the API refuses everyone else.
 */
import { MenuItem, Tab, Tabs, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useMemo, useState } from 'react'

import { useAccess } from 'app/hooks/useAccess'
import TeacherScorecard from 'app/pages/Audits/TeacherScorecard'
import {
  CUSTOM,
  LAST4,
  monthChoices,
  monthName
} from 'app/pages/Audits/TeacherScorecard/TeacherScorecard'
import { color } from 'app/theme/tokens'

import CdWeekPlan from './CdWeekPlan'
import PreschoolOpsOverview from './PreschoolOpsOverview'

export const SCORECARD_PERMISSION = 'centreAudit.teacherScorecard'

// A Period per tab (Satya, 29 Oct: "period in the section, not common at
// the top"): Operations and Teacher Scorecard each have their own, under
// the tabs; Actions has its Week of instead. Last 4 weeks = this week and the 3 before it (as the scorecard counts),
// a month = that month so far, or custom dates.
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
const plusDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + n)
  return isoDay(d)
}
export const periodDates = (range, custom, today = isoDay(new Date())) => {
  if (range === CUSTOM) {
    const [a, b] = [custom.from, custom.to].sort()
    return { from: a, to: b > today ? today : b }
  }
  if (range === LAST4) {
    const day = new Date(`${today}T00:00:00`).getDay()
    const monday = plusDays(today, -((day + 6) % 7))
    return { from: plusDays(monday, -21), to: today }
  }
  const last = isoDay(
    new Date(Number(range.slice(0, 4)), Number(range.slice(5, 7)), 0)
  )
  return { from: `${range}-01`, to: last > today ? today : last }
}

const useStyles = makeStyles(() => ({
  root: { padding: 24, display: 'flex', flexDirection: 'column', gap: 16 },
  // One line: tabs, then ⓘ / Score, Period and Centre (Satya, 28 Oct: the
  // ⓘ was wrapping onto a line of its own).
  head: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 16,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${color.border}`,
    paddingBottom: 8
  },
  // Tab names as typed, not ALL CAPS (Satya, 28 Oct).
  tabs: {
    minHeight: 40,
    minWidth: 0,
    '& .MuiTab-root': { textTransform: 'none' }
  },
  field: { minWidth: 180, background: '#fff' },
  slot: { alignItems: 'center', gap: 8 },
  headRight: {
    display: 'flex',
    flexWrap: 'nowrap',
    flexShrink: 0,
    gap: 12,
    alignItems: 'center'
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 16
  },
  card: {
    textAlign: 'left',
    font: 'inherit',
    cursor: 'pointer',
    background: color.surface,
    border: `2px solid ${color.border}`,
    borderRadius: 16,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    '&:hover': { borderColor: color.brand }
  },
  cardLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: color.textMuted,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  info: { fontSize: 18, cursor: 'help' },
  periodRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 12,
    alignItems: 'center'
  },
  big: { fontSize: 32, fontWeight: 700, lineHeight: 1.1 },
  sub: { fontSize: 13, color: color.ink }
}))

const startView = () => {
  try {
    const v = new URLSearchParams(window.location.search).get('view')
    // The Actions tab is gone (Shruti, 29 Oct): Week Plan replaces it, so
    // an old ?view=actions link opens Week Plan.
    if (v === 'actions') return 'week'
    return ['teachers', 'week'].includes(v) ? v : null
  } catch (e) {
    return null
  }
}

const newCustom = () => {
  const to = isoDay(new Date())
  return { from: plusDays(to, -27), to }
}

// One tab's Period: Last 4 weeks, a month, or custom dates.
const PeriodPicker = ({ range, onRange, custom, onCustom, classes }) => (
  <div className={classes.periodRow}>
    <TextField
      select
      size="small"
      variant="outlined"
      label="Period"
      className={classes.field}
      value={range}
      onChange={(e) => onRange(e.target.value)}
    >
      <MenuItem value={LAST4}>Last 4 weeks</MenuItem>
      {monthChoices().map((m) => (
        <MenuItem key={m} value={m}>
          {monthName(m)}
        </MenuItem>
      ))}
      <MenuItem value={CUSTOM}>Custom dates…</MenuItem>
    </TextField>
    {range === CUSTOM &&
      [
        ['from', 'From'],
        ['to', 'To']
      ].map(([k, text]) => (
        <TextField
          key={k}
          type="date"
          size="small"
          variant="outlined"
          label={text}
          style={{ background: '#fff' }}
          value={custom[k]}
          onChange={(e) =>
            e.target.value && onCustom((c) => ({ ...c, [k]: e.target.value }))
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: isoDay(new Date()) }}
        />
      ))}
  </div>
)

const PreschoolQuality = () => {
  const classes = useStyles()
  const { loaded, can } = useAccess()
  const canScorecard = loaded && can(SCORECARD_PERMISSION)
  const [view, setView] = useState(startView)
  const [opsSlot, setOpsSlot] = useState(null)
  const [scSlot, setScSlot] = useState(null)
  const [centre, setCentre] = useState('')
  const [centres, setCentres] = useState([])
  // Both open on the current month (Satya, 29 Oct); Last 4 weeks stays in
  // the list.
  const [opsRange, setOpsRange] = useState(() => monthChoices()[0])
  const [opsCustom, setOpsCustom] = useState(newCustom)
  const [range, setRange] = useState(() => monthChoices()[0])
  const [custom, setCustom] = useState(newCustom)
  const dates = useMemo(
    () => periodDates(opsRange, opsCustom),
    [opsRange, opsCustom]
  )

  const shown =
    view === 'week' || (view === 'teachers' && canScorecard) ? view : 'ops'
  const centreName = (centres.find((c) => c.uuid === centre) || {}).name || ''

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        {/* One row (Satya, 28 Oct): Operations | Teacher Scorecard | Week
            Plan, then Period and Centre. No page title: the top tab already
            says Quality and Ops. Week Plan (scorecard + ops) replaced the
            Actions tab (Shruti, 29 Oct). */}
        <Tabs
          value={shown}
          onChange={(e, v) => setView(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="off"
          className={classes.tabs}
        >
          <Tab value="ops" label="Operations" />
          {canScorecard && <Tab value="teachers" label="Teacher Scorecard" />}
          <Tab value="week" label="Week Plan" />
        </Tabs>
        <div className={classes.headRight}>
          {/* Each view puts its ⓘ (and the Ops score) here, beside Period. */}
          <span
            ref={setOpsSlot}
            className={classes.slot}
            style={{ display: shown === 'ops' ? 'flex' : 'none' }}
          />
          <span
            ref={setScSlot}
            className={classes.slot}
            style={{ display: shown === 'teachers' ? 'flex' : 'none' }}
          />
          {/* Each tab's own Period, in the header row beside Centre so
              there is no empty line above the cards (Satya, 29 Oct). */}
          {shown === 'ops' && (
            <PeriodPicker
              range={opsRange}
              onRange={setOpsRange}
              custom={opsCustom}
              onCustom={setOpsCustom}
              classes={classes}
            />
          )}
          {shown === 'teachers' && (
            <PeriodPicker
              range={range}
              onRange={setRange}
              custom={custom}
              onCustom={setCustom}
              classes={classes}
            />
          )}
          <TextField
            select
            size="small"
            variant="outlined"
            label="Centre"
            className={classes.field}
            value={centres.length ? centre : ''}
            disabled={!centres.length}
            onChange={(e) => setCentre(e.target.value)}
          >
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <div style={{ display: shown === 'ops' ? 'block' : 'none' }}>
        <PreschoolOpsOverview
          centre={centre}
          onCentre={setCentre}
          onCentres={setCentres}
          hideCentre
          headSlot={opsSlot}
          from={dates.from}
          to={dates.to}
        />
      </div>
      {shown === 'week' && (
        <CdWeekPlan
          centre={centre}
          centreName={centreName}
          canScorecard={canScorecard}
        />
      )}
      {canScorecard && (
        <div style={{ display: shown === 'teachers' ? 'block' : 'none' }}>
          <TeacherScorecard
            centreName={centreName}
            range={range}
            custom={custom}
            onRange={setRange}
            view="scorecard"
            headSlot={scSlot}
          />
        </div>
      )}
    </div>
  )
}

export default PreschoolQuality
